import { gql } from "@apollo/client";
import Stack from "atoms/Stack";
import TextWithTooltip from "atoms/TextWithTooltip";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING_HALF } from "helpers/spacings";
import useData from "helpers/useData";
import Section from "molecules/Section";
import StatsTable, {
  StatsTableHeaderCell,
  StatsTableRow,
  StatsTableStatCell,
  StatsTableStatCellFragment,
} from "molecules/StatsTable";
import { StatValueFragment } from "molecules/StatValue";
import React from "react";

export const BoxScoreFragment = gql`
  fragment BoxScoreFragment on Game {
    participations {
      id
      team {
        id
        name
      }
    }
  }
`;

export default function BoxScore({ game }) {
  const [data] = useData(
    gql`
      query BoxScore($gameId: ID!) {
        game(id: $gameId) {
          id
          participations {
            id
            isHomeTeam
            team {
              id
              name
            }
            overviewStats {
              shortName
              name
              ...StatValueFragment
            }
            gamePlayers {
              id
              playerNumber
              person {
                id
                fullNameWithAnonymisation
              }
              boxScoreStats {
                shortName
                name
                ...StatsTableStatCellFragment
              }
            }
            boxScoreTotalStats {
              shortName
              name
              ...StatsTableStatCellFragment
            }
          }
        }
      }
      ${StatValueFragment}
      ${StatsTableStatCellFragment}
    `,
    { gameId: game?.id },
    { skip: !game },
  );

  let homeParticipation = data?.game?.participations.find((participation) => participation.isHomeTeam);
  let awayParticipation = data?.game?.participations.find((participation) => !participation.isHomeTeam);

  // if there is no gamePlayer
  homeParticipation ||= data?.game?.participations[0];
  awayParticipation ||= data?.game?.participations[0];
  const firstRowHome = homeParticipation?.gamePlayers[0];
  const firstRowAway = awayParticipation?.gamePlayers[0];
  const homeTeamName = homeParticipation?.team.name || "Home Team";
  const awayTeamName = awayParticipation?.team.name || "Away Team";

  return (
    <Section title="BOX SCORE" labels>
      <Stack style={{ gap: SPACING_HALF }}>
        <div
          style={{
            fontFamily: FONT_FAMILY_GRAPHIK,
            fontSize: "0.9em",
            textTransform: "uppercase",
          }}
        >
          {homeTeamName}
        </div>
        <StatsTable
          headerColumns={["Players"]}
          columns={firstRowHome?.boxScoreStats.map((columnStat) => (
            <TextWithTooltip tooltip={columnStat.name} key={columnStat.shortName}>
              {columnStat.shortName}
            </TextWithTooltip>
          ))}
        >
          {homeParticipation?.gamePlayers.map((gamePlayer) => (
            <StatsTableRow key={gamePlayer.id}>
              <StatsTableHeaderCell>
                {gamePlayer.person.fullNameWithAnonymisation} - #{gamePlayer.playerNumber || "?"}
              </StatsTableHeaderCell>
              {gamePlayer.boxScoreStats.map((stat) => (
                <StatsTableStatCell key={stat.shortName} stat={stat} />
              ))}
            </StatsTableRow>
          ))}
          <StatsTableRow>
            <StatsTableHeaderCell>(Total)</StatsTableHeaderCell>
            {homeParticipation?.boxScoreTotalStats.map((stat) => (
              <StatsTableStatCell key={stat.shortName} stat={stat} />
            ))}
          </StatsTableRow>
        </StatsTable>
      </Stack>

      <Stack style={{ gap: SPACING_HALF, paddingTop: SPACING_HALF }}>
        <div
          style={{
            fontFamily: FONT_FAMILY_GRAPHIK,
            fontSize: "0.9em",
            textTransform: "uppercase",
          }}
        >
          {awayTeamName}
        </div>
        <StatsTable
          headerColumns={["Players"]}
          columns={firstRowAway?.boxScoreStats.map((columnStat) => (
            <TextWithTooltip tooltip={columnStat.name} key={columnStat.shortName}>
              {columnStat.shortName}
            </TextWithTooltip>
          ))}
        >
          {awayParticipation?.gamePlayers.map((gamePlayer) => (
            <StatsTableRow key={gamePlayer.id}>
              <StatsTableHeaderCell>
                {gamePlayer.person.fullNameWithAnonymisation} - #{gamePlayer.playerNumber || "?"}
              </StatsTableHeaderCell>
              {gamePlayer.boxScoreStats.map((stat) => (
                <StatsTableStatCell key={stat.shortName} stat={stat} />
              ))}
            </StatsTableRow>
          ))}
          <StatsTableRow>
            <StatsTableHeaderCell>(Total)</StatsTableHeaderCell>
            {awayParticipation?.boxScoreTotalStats.map((stat) => (
              <StatsTableStatCell key={stat.shortName} stat={stat} />
            ))}
          </StatsTableRow>
        </StatsTable>
      </Stack>
    </Section>
  );
}
